import FAQSchema from "@components/FAQSchema";
import OpenGraphImage from "@components/OpenGraphImage";
import ViewGalleryModal from "@components/ViewGalleryModal";
import PhotoWithTags from "@components/packageComponents/PhotoWithTags";
import {
  RAQ_PAGE_TYPE,
  RaqPageType,
  StorageLikesType,
} from "@components/utils/constants";
import useAppContext from "@hooks/useAppContext";
import useModalScrollFreeze from "@hooks/useModalScrollFreeze";
import CardHeaderAccessories from "@libs/cards/components/CardHeaderAccessories";
import { ITag } from "@modules/requestAQuote/components/TripPreferences";
import dynamic from "next/dynamic";
import React, { useCallback, useMemo, useState } from "react";

const Breadcrumb = dynamic(
  async () => await import("@modules/breadcrumb/Breadcrumb"),
  { ssr: false, loading: () => null }
);

/*
const GalleryHeader = dynamic(
  () => import("@libs/cards/GalleryHeader"), { ssr: false }
);


const PhotoWithTags = dynamic(
  () => import("@components/packageComponents/PhotoWithTags"), { ssr: false }
);
*/

interface IDetailsPageWrapper {
  data: any;
  children: React.ReactNode;
  likesType: StorageLikesType;
  pageType: RaqPageType;
}

const DetailsPageWrapper = ({
  data,
  children,
  likesType,
  pageType,
}: IDetailsPageWrapper): JSX.Element => {
  const appContext = useAppContext();

  const [viewGallery, setOpenGallery] = useState(false);

  const primaryImages = useMemo(() => {
    return data?.Media?.PrimaryImage || data?.PrimaryImage || [];
  }, [data]);

  const allImages = useMemo(() => {
    if (data?.Media?.Gallery) {
      return [...primaryImages, ...data.Media.Gallery];
    } else {
      return primaryImages;
    }
  }, [data, primaryImages]);

  const onGalleryClick = useCallback(() => {
    setOpenGallery(true);
  }, []);

  useModalScrollFreeze([viewGallery]);

  return (
    <>
      <OpenGraphImage
        image={primaryImages?.[0]}
        alt={data?.Long_Title}
      ></OpenGraphImage>
      {/* min-h-[60rem] is set to prevent initial view of footer (CLS) */}
      <div className="mt-8 min-h-[60rem] desktop:mx-[32px] desktop:mt-[4.5rem] desktop:max-w-[1376px] desktopLarge:mx-auto">
        <FAQSchema schema={data.FAQ_Schema} />

        <div className="flex flex-col">
          <div className="mx-5 h-[1.1875rem] max-w-[1376px] desktop:mx-0">
            <Breadcrumb />
          </div>
          <div className="flex flex-row items-center justify-between desktop:mb-6">
            <div>
              <h1 className="mb-3 mt-3 px-5 text-h1 font-semibold leading-[115%] desktop:max-w-[842px] desktop:px-0 desktop:leading-[120%]">
                {data.Long_Title}
              </h1>
              <div className="display:flex justify-between px-5 desktop:px-0">
                {data.Subhead ? (
                  <p className="mb-6 max-w-[950px] text-p14 font-normal text-darkgray tablet:pb-3 desktop:mb-0 desktop:block desktop:text-p">
                    {data.Subhead}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          {viewGallery && (
            <ViewGalleryModal
              viewGallery={viewGallery}
              setOpenGallery={setOpenGallery}
              allImages={allImages}
              title={data.Title}
            />
          )}
          <div className="min-h-[23rem] desktop:min-h-[33.75rem]">
            {/* appContext.isMobile && (
              <GalleryHeader
                id={data.id}
                title={data?.Long_Title}
                blurb={data?.Blurb}
                tags={data?.tags?.map((tag: ITag) => {
                  return {
                    id: tag.id,
                    title: tag.name,
                  };
                })}
                url={data?.SEO?.Slug}
                images={primaryImages}
                imageSize={"semilarge"}
                hasLike
                cardType={
                  Object.keys(CARD_TYPE_TO_LIKES_TYPE).find(
                    // @ts-expect-error
                    (key: StorageLikesType) =>
                      // @ts-expect-error
                      CARD_TYPE_TO_LIKES_TYPE[key] === likesType
                  ) as CardTypeType
                }
                onGalleryClick={onGalleryClick}
              />
            )*/}

            <div
              className={`relative h-full ${
                appContext.isMobile ? "ml-[0.6rem] px-[10px]" : "mb-[0.5rem]"
              }`}
            >
              <PhotoWithTags
                isSingle={pageType !== RAQ_PAGE_TYPE.PACKAGE || !data.locations}
                data={data}
                tags={data.tags}
                collectionType={pageType}
              />
              {appContext.isMobile && (
                <div className="pointer-events-none absolute top-0 left-[0.6rem] z-10 h-full w-[calc(100vw_-_20px)] desktop:hidden desktop:w-full">
                  <CardHeaderAccessories
                    onGalleryClick={onGalleryClick}
                    tags={data?.tags?.map((tag: ITag) => {
                      return {
                        id: tag.id,
                        title: tag.name,
                      };
                    })}
                    isGalleryHeader
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
    </>
  );
};

export default DetailsPageWrapper;
